/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { capitalize } from 'lodash'
import React from 'react'
import { useIntl } from 'react-intl'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import {
  EuiButtonEmpty,
  EuiComboBox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  getSecurityOptionsByProductTier,
  updateTableItemRoles,
} from '@modules/role-assignments-lib'
import type {
  ResourceRoleAssignment,
  ResourceType,
  RoleAssignmentsTableItem,
  RoleOptionDefinition,
  TableItemRoleDescriptor,
} from '@modules/role-assignments-lib/types'
import type { components, SecurityProductTier } from '@modules/project-user-api/v1/types'
import {
  deploymentOptions,
  elasticsearchOptions,
  observabilityOptions,
} from '@modules/role-assignments-lib/constants'
import { useGetProjectQuery } from '@modules/project-user-lib/hooks/get'
import { useGetProjectQuery as useGetProjectQueryAdmin } from '@modules/project-admin-lib/hooks/get'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useConfig } from '@modules/cui/ConfigContext'

const useGetOptions = () => {
  const { formatMessage } = useIntl()

  const getPredefinedGroup = (
    resourceType: ResourceType,
    productTier: SecurityProductTier | undefined,
    showAiSocEngineRoles: boolean,
  ) => {
    if (resourceType !== 'security' || !productTier || !showAiSocEngineRoles) {
      return {
        label: formatMessage({
          id: 'roles-assignments.predefined-roles-group.label',
          defaultMessage: 'Predefined roles',
        }),
        isGroupLabelOption: true,
        'data-test-subj': 'predefinedRolesGroup',
      }
    }

    if (productTier === 'search_ai_lake' || productTier === 'ai_soc_engine') {
      return {
        label: formatMessage({
          id: 'roles-assignments.security.ai-soc-engine.predefined-roles-group.label',
          defaultMessage: 'AI SOC Engine predefined roles',
        }),
        isGroupLabelOption: true,
        'data-test-subj': 'predefinedRolesGroup',
      }
    }

    const message = `${capitalize(productTier)} predefined roles`
    return {
      label: formatMessage({
        id: 'roles-assignments.security.predefined-roles-group.label',
        defaultMessage: message,
      }),
      isGroupLabelOption: true,
      'data-test-subj': 'predefinedRolesGroup',
    }
  }

  const noCustomRolesPlaceholder = {
    value: '',
    label: formatMessage({
      id: 'roles-assignments.no-custom-roles-placeholder',
      defaultMessage: 'No custom roles',
    }),
    disabled: true,
  } as EuiComboBoxOptionOption<string>

  const getPredefinedOptions = <T extends ResourceType, P extends SecurityProductTier>(
    resourceType: T,
    productTier: P | undefined,
  ): Array<EuiComboBoxOptionOption<string>> => {
    const getMappedPredefinedRoles = (predefinedRoles: RoleOptionDefinition[]) =>
      predefinedRoles.map(
        (roleDef) =>
          ({
            value: roleDef.roleId,
            label: formatMessage({ ...roleDef.labelDescriptor }),
            toolTipContent: formatMessage(
              { ...roleDef.descriptionDescriptor },
              { strong: (content) => <strong>{content}</strong>, br: <br /> },
            ),
          }) as EuiComboBoxOptionOption<string>,
      )

    switch (resourceType) {
      case 'deployment':
        return getMappedPredefinedRoles(deploymentOptions)
      case 'elasticsearch':
        return getMappedPredefinedRoles(elasticsearchOptions)
      case 'observability':
        return getMappedPredefinedRoles(observabilityOptions)
      case 'security':
        return getMappedPredefinedRoles(getSecurityOptionsByProductTier(productTier))
      default:
        return []
    }
  }

  const getOptions = <T extends ResourceType, P extends SecurityProductTier>(
    resourceType: T,
    productTier: P | undefined,
    displayCustomRoles: boolean,
    displayAiSocEngineRoles: boolean,
    customRoleOptions: Array<EuiComboBoxOptionOption<string>>,
    roleManagementUrl?: string,
  ): Array<EuiComboBoxOptionOption<string>> => {
    const predefinedOptions = getPredefinedOptions(
      resourceType,
      displayAiSocEngineRoles ? productTier : 'complete',
    )

    if (resourceType === 'deployment') {
      return predefinedOptions
    }

    const customRolesGroup = {
      label: formatMessage({
        id: 'roles-assignments.custom-roles-group.label',
        defaultMessage: 'Custom roles',
      }),
      isGroupLabelOption: true,
      ...(roleManagementUrl !== undefined && {
        append: <AddRoleLinkButton roleManagementUrl={roleManagementUrl} />,
      }),
      'data-test-subj': 'customRolesGroup',
    }

    const customOptionsToDisplay =
      customRoleOptions.length > 0 ? customRoleOptions : [noCustomRolesPlaceholder]

    // To Do: remove observability conditions below when custom roles are enabled for oblt projects
    return [
      ...(displayCustomRoles
        ? [getPredefinedGroup(resourceType, productTier, displayAiSocEngineRoles)]
        : []),
      ...predefinedOptions,
      ...(displayCustomRoles ? [customRolesGroup] : []),
      ...(displayCustomRoles ? customOptionsToDisplay : []),
    ]
  }

  const noRolesPlaceholder = (singleSelection: boolean) =>
    formatMessage(
      {
        id: 'roles-assignments.none.placeholder',
        defaultMessage: 'No {count, plural, one {role} other {roles}} assigned',
      },
      {
        count: singleSelection ? 1 : 2,
      },
    )

  return { getOptions, getPredefinedOptions, noRolesPlaceholder }
}

const getSelectedOptions = (
  roles: TableItemRoleDescriptor[],
  resourceType: ResourceType,
  projectInfo: { id?: string; tier?: SecurityProductTier },
  options: Array<EuiComboBoxOptionOption<string>>,
  multipleSelectionEnabled: boolean,
  showAiSocEngineRoles: boolean,
): Array<EuiComboBoxOptionOption<string>> => {
  const selectedOptions: Array<EuiComboBoxOptionOption<string>> = []
  const securityProjectSpecificRoleIds =
    projectInfo.id && projectInfo.tier && resourceType === 'security'
      ? {
          [projectInfo.id]: getSecurityOptionsByProductTier(projectInfo.tier).map((r) => r.roleId),
        }
      : undefined

  if (roles.length) {
    const rolesToProcess = multipleSelectionEnabled ? roles : roles.slice(0, 1)

    rolesToProcess.forEach((role) => {
      const found = options.find((option) => option.value === role.roleId)
      const isFoundCustomRole = roles.find(
        (r) => r.roleId === found?.value && r.isCustomRole,
      )?.roleId

      const checkSecurityProjectRoles =
        (securityProjectSpecificRoleIds &&
          projectInfo.id &&
          projectInfo.tier &&
          securityProjectSpecificRoleIds[projectInfo.id]?.includes(found?.value ?? '')) ||
        isFoundCustomRole

      if (found) {
        if (!showAiSocEngineRoles || checkSecurityProjectRoles || resourceType !== 'security') {
          selectedOptions.push(found)
        }
      }
    })
  }

  return selectedOptions
}

const AddRoleLinkButton = ({ roleManagementUrl }: { roleManagementUrl: string }) => (
  <EuiButtonEmpty css={{ marginLeft: 'auto' }} flush='right'>
    <EuiIcon type='plusCircle' size='s' color='primary' css={{ marginRight: '8px' }} />
    <EuiLink href={roleManagementUrl}>
      <FormattedMessage
        id='roles-assignments.add-custom-role.message'
        defaultMessage='Add custom role'
      />
    </EuiLink>
  </EuiButtonEmpty>
)

export interface RoleSelectorProps<T extends ResourceType, R extends ResourceRoleAssignment<T>> {
  resourceType: T
  items: Array<RoleAssignmentsTableItem<T>>
  tableItemsToRoleAssignment: (items: Array<RoleAssignmentsTableItem<T>>) => R[]
  onChangeRoleAssignments: (roleAssignments: Array<ResourceRoleAssignment<T>>) => void
  roles: TableItemRoleDescriptor[]
  id: string
  roleManagementUrl?: string
  isDisabled: boolean
  customRolesEnabled: boolean
  observabilityCustomRoles: boolean
  projectRolesQueryData: components['schemas']['ProjectRolesByProjectID'] | undefined
}

export const RoleSelector = <T extends ResourceType, R extends ResourceRoleAssignment<T>>(
  props: RoleSelectorProps<T, R>,
) => {
  const {
    resourceType,
    items,
    tableItemsToRoleAssignment,
    onChangeRoleAssignments,
    roles,
    id: projectId,
    roleManagementUrl,
    isDisabled,
    customRolesEnabled,
    observabilityCustomRoles,
    projectRolesQueryData,
  } = props

  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'

  const [isFlagReady, flags] = useFlagsWhenLoaded()
  const showAiSocEngineRoles = isFlagReady && flags.aiSocRoles

  const { getOptions, getPredefinedOptions, noRolesPlaceholder } = useGetOptions()
  const predefinedRoles = getPredefinedOptions(resourceType, undefined)

  const singleSelection = resourceType === 'deployment' || !customRolesEnabled

  const displayCustomRoles =
    {
      elasticsearch: customRolesEnabled,
      observability: customRolesEnabled && observabilityCustomRoles,
      security: customRolesEnabled,
    }[resourceType as string] ?? false

  const displayAiSocEngineRoles =
    {
      elasticsearch: false,
      observability: false,
      security: showAiSocEngineRoles,
    }[resourceType as string] ?? false

  const customRoleOptions: Array<EuiComboBoxOptionOption<string>> = (
    projectRolesQueryData?.[projectId]?.roles ?? []
  )
    .map(
      (role: { name: string; description: string }) =>
        ({
          value: role.name,
          label: role.name,
          toolTipContent: role.description,
          title: '',
        }) as EuiComboBoxOptionOption<string>,
    )
    .sort((a: EuiComboBoxOptionOption<string>, b: EuiComboBoxOptionOption<string>) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    )

  const enableSecurityProjectQuery =
    !!projectId && resourceType === 'security' && showAiSocEngineRoles

  const useGetSecurityProjectUser = useGetProjectQuery('security', projectId, {
    enabled: !isAdminConsole && enableSecurityProjectQuery,
  })

  const useGetSecurityProjectAdmin = useGetProjectQueryAdmin('security', projectId, {
    enabled: isAdminConsole && enableSecurityProjectQuery,
  })

  const securityProjectData = isAdminConsole
    ? useGetSecurityProjectAdmin.data
    : useGetSecurityProjectUser.data

  const securityProjectInfo = {
    id: securityProjectData?.id,
    tier:
      securityProjectData && securityProjectData.type === 'security'
        ? securityProjectData.product_types?.[0]?.product_tier
        : undefined,
  }
  const productTier = items.find((item) => item.id === projectId)?.productTier
  const options = getOptions(
    resourceType,
    productTier,
    displayCustomRoles,
    displayAiSocEngineRoles,
    customRoleOptions,
    roleManagementUrl,
  )

  return (
    <EuiFlexGroup>
      <EuiFlexItem>
        <EuiComboBox<string>
          data-test-subj='resourceRoleSelector'
          data-test-id={`resourceRoleSelector-${projectId}`}
          isDisabled={isDisabled}
          placeholder={noRolesPlaceholder(singleSelection)}
          singleSelection={singleSelection}
          options={options}
          selectedOptions={getSelectedOptions(
            roles,
            resourceType,
            securityProjectInfo,
            options,
            customRolesEnabled,
            showAiSocEngineRoles,
          )}
          onChange={(selectedOptions) => {
            const selectedRoleDescriptors = selectedOptions.map<TableItemRoleDescriptor>(
              (roleOption) => ({
                roleId: roleOption.value ?? '',
                isCustomRole: predefinedRoles.every((role) => role.value !== roleOption.value),
              }),
            )
            const apiAccess = items.find((item) => item.id === projectId)?.apiAccess
            updateTableItemRoles(
              items,
              projectId,
              selectedRoleDescriptors,
              tableItemsToRoleAssignment,
              onChangeRoleAssignments,
              apiAccess,
            )
          }}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
